import { useEffect, useRef } from 'react'
import type { StorageLocation } from '@/api/pending'
import { CARRIER_CHOICES, MAX_PHOTOS, STORAGE_LOCATIONS } from '@/lib/desk'
import { Chip, Eyebrow, Field, Icon } from '@/ui'
import styles from './desk.module.css'
import { deskStrings } from './strings'

export function CarrierChips({ value, onPick }: { value: string; onPick: (c: string) => void }) {
  return (
    <div className={styles.pad}>
      <Eyebrow>{deskStrings.register.carrier}</Eyebrow>
      <div className={styles.chips}>
        {CARRIER_CHOICES.map((c) => (
          <Chip key={c} selected={c === value} onClick={() => onPick(c)}>
            {c}
          </Chip>
        ))}
      </div>
    </div>
  )
}

interface StorageFieldsProps {
  location: StorageLocation
  slot: string
  onLocation: (l: StorageLocation) => void
  onSlot: (slot: string) => void
}

/** "Onde fica guardado": applies to the whole registration. The compartment shows for Armário. */
export function StorageFields({ location, slot, onLocation, onSlot }: StorageFieldsProps) {
  const t = deskStrings.register
  return (
    <div className={styles.pad}>
      <Eyebrow>{t.storageTitle}</Eyebrow>
      <div className={styles.chips}>
        {STORAGE_LOCATIONS.map((l) => (
          <Chip key={l.id} selected={l.id === location} onClick={() => onLocation(l.id)}>
            {l.label}
          </Chip>
        ))}
      </div>
      {location === 'locker' && (
        <Field
          label={t.slotLabel}
          value={slot}
          onChange={(v) => onSlot(v.replace(/\D/g, '').slice(0, 4))}
          placeholder={t.slotPlaceholder}
          autoFocus
        />
      )}
      <span className={styles.muted}>{t.storageHint}</span>
    </div>
  )
}

export interface PickedPhoto {
  file: File
  url: string
}

/** Wraps the browser API: absent in some test environments. */
function makePhoto(file: File): PickedPhoto {
  const url = typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : ''
  return { file, url }
}

/** Optional photos, up to 4, with thumbnails. Uploaded on submit by the parent. */
export function PhotoPicker({
  photos,
  onChange,
}: {
  photos: PickedPhoto[]
  onChange: (photos: PickedPhoto[]) => void
}) {
  const t = deskStrings.register
  const latest = useRef(photos)
  useEffect(() => {
    latest.current = photos
  })
  // Object URLs live as long as the picker does.
  useEffect(
    () => () => {
      for (const p of latest.current) if (p.url) URL.revokeObjectURL(p.url)
    },
    [],
  )

  function add(files: FileList | null) {
    if (!files) return
    const room = MAX_PHOTOS - photos.length
    const next = [...files].filter((f) => f.type.startsWith('image/')).slice(0, room)
    if (next.length) onChange([...photos, ...next.map(makePhoto)])
  }

  function remove(photo: PickedPhoto) {
    if (photo.url) URL.revokeObjectURL(photo.url)
    onChange(photos.filter((p) => p !== photo))
  }

  return (
    <div className={styles.pad}>
      <div className={styles.labelLine}>
        <Eyebrow>{t.photosTitle}</Eyebrow>
        <span className={styles.muted}>{t.photosOptional}</span>
      </div>
      <div className={styles.photos}>
        {photos.map((p, i) => (
          <div key={`${i}-${p.file.name}`} className={styles.thumb}>
            {p.url && <img src={p.url} alt={`Foto ${i + 1}`} />}
            <button
              type="button"
              className={styles.thumbRemove}
              aria-label={`Remover foto ${i + 1}`}
              onClick={() => remove(p)}
            >
              <Icon name="close" size={14} />
            </button>
          </div>
        ))}
        {photos.length < MAX_PHOTOS && (
          <label className={styles.addPhoto}>
            <Icon name="photo_camera" size={20} />
            {photos.length ? 'Mais' : 'Foto'}
            <input
              type="file"
              accept="image/*"
              multiple
              className={styles.fileInput}
              aria-label={t.addPhoto}
              onChange={(e) => {
                add(e.target.files)
                e.target.value = ''
              }}
            />
          </label>
        )}
      </div>
      <span className={styles.muted}>{t.photosHint}</span>
    </div>
  )
}
